import React from 'react';
import { Settings2 } from 'lucide-react';
import {
  DEFAULT_TIMELINE_ASSUMPTIONS,
  TimelineAssumptions,
  saveTimelineAssumptions,
} from '../../utils/ongoingTrialsTimeline';

interface TimelineAssumptionsPanelProps {
  assumptions: TimelineAssumptions;
  onChange: (next: TimelineAssumptions) => void;
}

const TimelineAssumptionsPanel: React.FC<TimelineAssumptionsPanelProps> = ({ assumptions, onChange }) => {
  const [open, setOpen] = React.useState(false);

  const update = (patch: Partial<TimelineAssumptions>) => {
    const next = { ...assumptions, ...patch };
    onChange(next);
    saveTimelineAssumptions(next);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/80">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-100/80 rounded-xl"
      >
        <span className="inline-flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-slate-500" />
          Timeline assumptions
        </span>
        <span className="text-xs font-medium text-slate-500">{open ? 'Hide' : 'Edit'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <label className="space-y-1">
            <span className="font-semibold text-slate-600">Filing after primary completion (months)</span>
            <input
              type="number"
              min={0}
              max={24}
              step={0.5}
              value={assumptions.filingMonthsAfterPrimary}
              onChange={(e) => update({ filingMonthsAfterPrimary: Number(e.target.value) || 0 })}
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            />
            <span className="text-slate-500">Default 3.5 (3–4 mo)</span>
          </label>
          <label className="space-y-1">
            <span className="font-semibold text-slate-600">Standard review after filing (months)</span>
            <input
              type="number"
              min={0}
              max={24}
              step={1}
              value={assumptions.standardReviewMonths}
              onChange={(e) => update({ standardReviewMonths: Number(e.target.value) || 0 })}
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            />
            <span className="text-slate-500">Default 9 mo</span>
          </label>
          <label className="space-y-1">
            <span className="font-semibold text-slate-600">Accelerated review after filing (months)</span>
            <input
              type="number"
              min={0}
              max={24}
              step={1}
              value={assumptions.acceleratedReviewMonths}
              onChange={(e) => update({ acceleratedReviewMonths: Number(e.target.value) || 0 })}
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            />
            <span className="text-slate-500">Default 6 mo</span>
          </label>
          <div className="sm:col-span-3 flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => onChange({ ...DEFAULT_TIMELINE_ASSUMPTIONS })}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
            >
              Reset to defaults
            </button>
            <p className="text-slate-500 text-[11px]">
              Data readout = primary completion date. Toggle accelerated review per trial in the timeline rows.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineAssumptionsPanel;
